import { Button } from "@/components/ui/button";
import { useLocation } from "wouter";
import { Home, ArrowLeft, Search } from "lucide-react";

export default function NotFound() {
  const [, setLocation] = useLocation();

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-gradient-to-br from-background via-blue-50/30 to-muted relative overflow-hidden dark:via-blue-950/10">
      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-24 -right-24 w-96 h-96 bg-blue-100/40 rounded-full blur-3xl" />
        <div className="absolute -bottom-32 -left-32 w-[30rem] h-[30rem] bg-indigo-100/30 rounded-full blur-3xl" />
        <div className="absolute top-1/3 left-1/4 w-64 h-64 bg-purple-100/20 rounded-full blur-3xl" />

        {/* Grid pattern overlay */}
        <div
          className="absolute inset-0 opacity-[0.015]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(0,0,0,1) 1px, transparent 1px), linear-gradient(90deg, rgba(0,0,0,1) 1px, transparent 1px)",
            backgroundSize: "60px 60px",
          }}
        />
      </div>

      <div className="relative z-10 flex flex-col items-center text-center px-6 max-w-lg mx-auto">
        {/* Brand mark */}
        <div className="animate-fadeIn stagger-1 mb-8 flex items-center gap-2 text-muted-foreground">
          <img src="/logo/singleLogo.png" alt="Dynamatix" className="w-7 h-7" />
          <span className="text-sm font-medium tracking-wide">Dynamatix</span>
        </div>

        {/* Large 404 number */}
        <div className="animate-fadeIn stagger-2 relative mb-2 select-none">
          <h1 className="text-[10rem] sm:text-[12rem] font-black leading-none tracking-tighter bg-gradient-to-b from-muted-foreground/30 to-muted-foreground/20 bg-clip-text text-transparent">
            404
          </h1>
          {/* Floating search icon */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
            <div className="w-16 h-16 rounded-2xl bg-cardshadow-lg shadow-blue-500/10 border border-border flex items-center justify-center animate-scaleIn">
              <Search className="w-7 h-7 text-primary" />
            </div>
          </div>
        </div>

        {/* Message */}
        <div className="animate-fadeIn stagger-3">
          <h2 className="text-xl sm:text-2xl font-semibold text-foreground mb-2">
            Page not found
          </h2>
          <p className="text-muted-foreground text-sm sm:text-base leading-relaxed max-w-sm mx-auto">
            The page you're looking for doesn't exist or may have been moved to a different location.
          </p>
        </div>

        {/* Action buttons */}
        <div className="animate-fadeIn stagger-4 mt-8 flex flex-col sm:flex-row items-center gap-3 w-full sm:w-auto">
          <Button
            onClick={() => setLocation("/")}
            className="w-full sm:w-auto gap-2 px-6 shadow-md shadow-primary/20 hover:shadow-lg hover:shadow-primary/25 transition-all"
            size="lg"
          >
            <Home className="w-4 h-4" />
            Back to Dashboard
          </Button>
          <Button
            variant="outline"
            onClick={() => window.history.back()}
            className="w-full sm:w-auto gap-2 px-6"
            size="lg"
          >
            <ArrowLeft className="w-4 h-4" />
            Go Back
          </Button>
        </div>

        {/* URL hint */}
        <div className="animate-fadeIn stagger-5 mt-10 text-xs text-muted-foreground">
          <span className="px-3 py-1.5 bg-card/60 backdrop-blur-sm rounded-full border border-border/60 font-mono">
            {window.location.pathname}
          </span>
        </div>
      </div>
    </div>
  );
}
